import { parseAdManifest } from './adManifest';
import { AD_FORMATS, AdSlotType, type AdCampaign, type AdCreative } from './AdTypes';

export interface AdRequestContext {
  readonly now: Date;
  readonly runDistance: number;
  /** Stable id of the slot instance – allows rotation without repeating the same creative. */
  readonly slotInstanceId: string;
}

export interface AdSelection {
  readonly campaign: AdCampaign;
  readonly creative: AdCreative;
}

/**
 * Source of campaigns/creatives. The game only knows this interface:
 * ManifestAdProvider (JSON manifest with image creatives – static file now, ad backend later)
 * on top of StaticAdProvider (scheduling, weighting, impression caps).
 */
export interface AdProvider {
  select(slotType: AdSlotType, ctx: AdRequestContext): AdSelection | null;
  /** True: every slot of this type carries an ad (a sponsored game), regardless of the fill rate. */
  fillsEverySlot?(slotType: AdSlotType): boolean;
  /** Called when an impression was counted (caps, pacing). */
  recordImpression?(campaignId: string): void;
}

/** Filters campaigns by slot type and flight dates, then picks weighted and round-robin. */
export class StaticAdProvider implements AdProvider {
  private readonly impressions = new Map<string, number>();
  private rotation = 0;

  /**
   * @param random given: campaigns are mixed at random by weight (booked campaigns, ructec 2026-10-05: "per
   *        Zufallsgenerator mischen"); without it the deterministic weighted round-robin (house ads, tests).
   */
  constructor(
    private readonly campaigns: readonly AdCampaign[],
    private readonly random?: () => number,
  ) {}

  select(slotType: AdSlotType, ctx: AdRequestContext): AdSelection | null {
    const now = ctx.now.getTime();
    const eligible = this.campaigns.filter(
      (c) =>
        c.slotTypes.includes(slotType) &&
        Date.parse(c.startDate) <= now &&
        now <= Date.parse(c.endDate) &&
        (c.maxImpressions === undefined || (this.impressions.get(c.campaignId) ?? 0) < c.maxImpressions),
    );
    if (eligible.length === 0) return null;

    if (this.random) {
      // plain weighted random pick: every running booking the same chance, ructec by its free places (no "never twice
      // in a row" – that would hand a single booking half of all slots instead of its share)
      const total = eligible.reduce((sum, c) => sum + Math.max(1, c.weight), 0);
      let ticket = this.random() * total;
      const campaign = eligible.find((c) => (ticket -= Math.max(1, c.weight)) < 0) ?? eligible[eligible.length - 1]!;
      const creatives = campaign.creatives.filter((cr) => cr.slotTypes.includes(slotType));
      const creative = creatives[Math.floor(this.random() * creatives.length)];
      return creative ? { campaign, creative } : null;
    }

    // Deterministic weighted round-robin: avoids the same creative on neighbouring slots.
    const totalWeight = eligible.reduce((sum, c) => sum + Math.max(1, c.weight), 0);
    let ticket = this.rotation++ % totalWeight;
    for (const campaign of eligible) {
      ticket -= Math.max(1, campaign.weight);
      if (ticket < 0) {
        const creatives = campaign.creatives.filter((cr) => cr.slotTypes.includes(slotType));
        const creative = creatives[this.rotation % Math.max(1, creatives.length)];
        return creative ? { campaign, creative } : null;
      }
    }
    return null;
  }

  recordImpression(campaignId: string): void {
    this.impressions.set(campaignId, (this.impressions.get(campaignId) ?? 0) + 1);
  }
}

/** Long-running flight of the house campaign (until real bookings replace it). */
const HOUSE_FLIGHT = { startDate: '2026-01-01T00:00:00Z', endDate: '2099-12-31T23:59:59Z' } as const;
/** ructec creative per ad format (tools/blender/assets/AD_RuctecBanner_A.py → public/ads/house/). */
const RUCTEC_CREATIVES: readonly (readonly [string, readonly AdSlotType[]])[] = [
  ['4x1', [AdSlotType.FenceLeft, AdSlotType.FenceRight]],
  ['3x1', [AdSlotType.ScaffoldHigh]],
  ['2.4x1', [AdSlotType.ContainerLeft, AdSlotType.ContainerRight]],
  ['5x1', [AdSlotType.CraneBanner]],
  ['1x2', [AdSlotType.BuildingBanner]],
  ['1.4x1', [AdSlotType.BuildingWrap]],
];
/** Gantry banner over the road (AD_GANTRY_BANNER, 4:1, 2560 × 640 px) – same file as HOUSE_RUCTEC_GANTRY in campaigns.json. */
const RUCTEC_GANTRY_CREATIVE_URL = '/ads/house/ructec_gantry_4x1.jpg';
/** Placeholder look of the house creatives (shown if an image fails to load). */
const RUCTEC_LOOK = { label: 'ructec.com', backgroundColor: '#172441', textColor: '#f59f0a' } as const;

/**
 * House advertising: ructec in every slot format. Built in, so the game shows ructec even when
 * the ad manifest (public/ads/campaigns.json, later the backend) cannot be loaded.
 */
export const HOUSE_CAMPAIGNS: readonly AdCampaign[] = [
  {
    campaignId: 'HOUSE_RUCTEC',
    companyId: 'RUCTEC',
    companyName: 'ructec GmbH',
    ...HOUSE_FLIGHT,
    weight: 1,
    slotTypes: Object.values(AdSlotType),
    creatives: [
      ...RUCTEC_CREATIVES.map(([format, slotTypes]) => ({
        creativeId: `HOUSE_RUCTEC_${format.replace('.', '_')}`,
        campaignId: 'HOUSE_RUCTEC',
        textureUrl: `/ads/house/ructec_${format}.jpg`,
        slotTypes,
        ...RUCTEC_LOOK,
      })),
      {
        creativeId: 'HOUSE_RUCTEC_GANTRY',
        campaignId: 'HOUSE_RUCTEC',
        textureUrl: RUCTEC_GANTRY_CREATIVE_URL,
        slotTypes: [AdSlotType.GantryBanner],
        ...RUCTEC_LOOK,
      },
    ],
  },
];

const POSTER_PATH = '/storage/v1/object/public/spiel-werbung/';

/** Relative game assets, or posters from the public poster bucket of one of the configured ad backends. */
export function textureAllowed(textureUrl: string, allowedOrigins: readonly string[]): boolean {
  if (textureUrl.startsWith('/') && !textureUrl.startsWith('//')) return true;
  try {
    const u = new URL(textureUrl);
    return u.protocol === 'https:' && allowedOrigins.includes(u.origin) && u.pathname.startsWith(POSTER_PATH);
  } catch {
    return false;
  }
}

/**
 * A sponsored game shows the company on EVERY ad surface (ructec 2026-10-05: "immer komplett jedes Werbeplakat von dieser
 * Firma"). A booking brings posters for three formats; every other format (scaffold 3:1, container 2.4:1, crane 5:1,
 * building 1:2) shows the poster whose ratio is closest, cropped to its centre (AdCreative.sourceAspect). The campaign ids
 * stay those of the booking, so a run is counted as before.
 */
export function coverEveryFormat(campaigns: readonly AdCampaign[]): AdCampaign[] {
  const posters = campaigns.flatMap((c) => c.creatives.filter((cr) => cr.slotTypes.length > 0).map((cr) => ({ campaign: c, creative: cr, aspect: AD_FORMATS[cr.slotTypes[0]!].aspectRatio })));
  if (posters.length === 0) return [...campaigns];
  const covered = new Set(campaigns.flatMap((c) => c.slotTypes));
  const extra = new Map<string, { slotTypes: AdSlotType[]; creatives: AdCreative[] }>();
  for (const slotType of Object.values(AdSlotType)) {
    if (covered.has(slotType)) continue;
    const target = AD_FORMATS[slotType].aspectRatio;
    const best = posters.reduce((a, b) => (Math.abs(Math.log(b.aspect / target)) < Math.abs(Math.log(a.aspect / target)) ? b : a));
    const add = extra.get(best.campaign.campaignId) ?? { slotTypes: [], creatives: [] };
    add.slotTypes.push(slotType);
    add.creatives.push({ ...best.creative, creativeId: `${best.creative.creativeId}_${slotType}`, slotTypes: [slotType], sourceAspect: best.aspect });
    extra.set(best.campaign.campaignId, add);
  }
  return campaigns.map((c) => {
    const add = extra.get(c.campaignId);
    return add ? { ...c, slotTypes: [...c.slotTypes, ...add.slotTypes], creatives: [...c.creatives, ...add.creatives] } : c;
  });
}

/** Booked surface → slot types (same mapping as the backend's spiel_werbung_manifest). */
const SURFACE_SLOTS: Readonly<Record<string, readonly AdSlotType[]>> = {
  SEITENBANNER: [AdSlotType.FenceLeft, AdSlotType.FenceRight],
  WERBEBRUECKE: [AdSlotType.GantryBanner],
  KREUZUNG: [AdSlotType.BuildingWrap],
};

/**
 * ructec's share per booked surface (`hausGewicht` of the backend: free places × average remaining views) – the house
 * creatives keep rotating with the bookings in that ratio. Slot types without a booking show the house ad anyway.
 */
export function houseShares(json: unknown): AdCampaign[] {
  const weights = typeof json === 'object' && json !== null ? (json as { hausGewicht?: unknown }).hausGewicht : undefined;
  if (typeof weights !== 'object' || weights === null) return [];
  const house = HOUSE_CAMPAIGNS[0]!;
  const shares: AdCampaign[] = [];
  for (const [surface, raw] of Object.entries(weights as Record<string, unknown>)) {
    const slots = SURFACE_SLOTS[surface];
    const weight = typeof raw === 'number' ? Math.round(raw) : 0;
    if (!slots || weight <= 0) continue;
    shares.push({
      ...house,
      campaignId: `${house.campaignId}_${surface}`,
      weight,
      slotTypes: slots,
      creatives: house.creatives.filter((c) => c.slotTypes.some((s) => slots.includes(s))),
    });
  }
  return shares;
}

/** Company of a running booking shown as "sponsored by" (start screen and HUD). */
export interface AdSponsor {
  readonly id: string;
  readonly name: string;
  /** Logo from the ad backend's poster bucket, or null (name only). */
  readonly logoUrl: string | null;
  /** Website of the company (http/https only), opened in a new tab from the badge – or null. */
  readonly website: string | null;
}

/** A web address the badge may link to: http(s), no credentials, at most 300 characters – otherwise null. */
export function safeWebsite(raw: unknown): string | null {
  if (typeof raw !== 'string' || raw.length > 300) return null;
  try {
    const u = new URL(/^https?:\/\//i.test(raw.trim()) ? raw.trim() : `https://${raw.trim()}`);
    if ((u.protocol !== 'https:' && u.protocol !== 'http:') || u.username || u.password || !u.hostname.includes('.')) return null;
    return u.href;
  } catch {
    return null;
  }
}

/**
 * Short text of a website for the badge: without protocol and "www.", a trailing slash dropped; longer than `max`
 * characters → cut with "…" (user 2026-10-05: "ist die Website-Adresse extrem lang kürzen wir sie … aus.punkt.punkt").
 */
export function shortWebsite(url: string, max = 28): string {
  const text = url.replace(/^https?:\/\//i, '').replace(/^www\./i, '').replace(/\/$/, '');
  return text.length <= max ? text : `${text.slice(0, max - 1)}…`;
}

/**
 * A sponsor logo: a copy in the poster bucket, or the company-profile logo the ad backend serves itself
 * (`/functions/v1/spiel-werbung?logo=<this sponsor>` on a configured backend origin) – nothing else.
 */
function logoAllowed(url: string, sponsorId: string, allowedImageOrigins: readonly string[]): boolean {
  if (textureAllowed(url, allowedImageOrigins)) return true;
  try {
    const u = new URL(url);
    return u.protocol === 'https:' && allowedImageOrigins.includes(u.origin) && u.pathname === '/functions/v1/spiel-werbung' && u.search === `?logo=${sponsorId}`;
  } catch {
    return false;
  }
}

/** Sponsors of the manifest (`sponsoren`) – only well-formed entries, logos only from the allowed poster buckets. */
export function parseSponsors(json: unknown, allowedImageOrigins: readonly string[]): AdSponsor[] {
  const raw = typeof json === 'object' && json !== null ? (json as { sponsoren?: unknown }).sponsoren : undefined;
  if (!Array.isArray(raw)) return [];
  const sponsors: AdSponsor[] = [];
  for (const entry of raw.slice(0, 50)) {
    if (typeof entry !== 'object' || entry === null) continue;
    const { id, name, logoUrl, website } = entry as { id?: unknown; name?: unknown; logoUrl?: unknown; website?: unknown };
    if (typeof id !== 'string' || !/^B_[0-9a-f]{32}$/.test(id) || typeof name !== 'string') continue;
    const clean = name.trim().slice(0, 80);
    if (!clean) continue;
    const logo = typeof logoUrl === 'string' && /^https:/.test(logoUrl) && logoAllowed(logoUrl, id, allowedImageOrigins) ? logoUrl : null;
    sponsors.push({ id, name: clean, logoUrl: logo, website: safeWebsite(website) });
  }
  return sponsors;
}

/**
 * Campaigns from the ad manifest (JSON with JPEG/PNG creatives – a static file now, the ad backend
 * later). Until it is loaded, and if it fails or is empty, the built-in house campaigns are shown.
 */
export class ManifestAdProvider implements AdProvider {
  private readonly fallback: StaticAdProvider;
  private delegate: StaticAdProvider;

  /** Session token of the ad backend (signed, ~3 h) – the reporter needs it to have impressions counted. */
  session: string | null = null;

  /** Companies of the running bookings – empty without bookings. */
  sponsors: readonly AdSponsor[] = [];

  /**
   * The company this player's game belongs to (ructec 2026-10-05: "das ganze Spiel dann von dieser einen Firma … per
   * Zufallsgenerator … immer wenn ein neuer Spieler das Spiel startet"): drawn at random when the game loads, kept on
   * every reload while its booking runs – every booked surface shows only its posters, plus "sponsored by". Null: ructec.
   */
  sponsor: AdSponsor | null = null;

  /** Every ad surface carries the sponsor's poster – no bare fences, no ructec while a company sponsors the game. */
  fillsEverySlot(_slotType: AdSlotType): boolean {
    return this.sponsor !== null && this.delegate !== this.fallback;
  }

  /**
   * @param allowedImageOrigins fixed origins whose poster bucket may serve creatives (the configured ad backends) –
   *        never derived from the manifest URL itself.
   */
  constructor(
    fallbackCampaigns: readonly AdCampaign[],
    private readonly allowedImageOrigins: readonly string[] = [],
    /** Mixes the booked campaigns (and ructec's free places) at random. */
    private readonly random: () => number = Math.random,
  ) {
    this.fallback = new StaticAdProvider(fallbackCampaigns);
    this.delegate = this.fallback;
  }

  /**
   * Fetches and validates the manifest; returns the problems found (never throws). Anything but a valid manifest with
   * at least one campaign puts the house ads back (an emergency stop or a blocked booking never keeps showing).
   */
  async load(url: string, timeoutMs: number, fetchFn: typeof fetch = fetch): Promise<string[]> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    const previous = this.sponsor?.id ?? null;
    this.delegate = this.fallback;
    this.session = null;
    this.sponsors = [];
    this.sponsor = null;
    try {
      const response = await fetchFn(url, { signal: controller.signal, cache: 'no-store' });
      if (!response.ok) return [`ad manifest ${url}: HTTP ${response.status}`];
      const json: unknown = await response.json();
      const { campaigns, errors } = parseAdManifest(json);
      // only images from the game itself or the configured ad backends' poster bucket
      const safe = campaigns
        .map((c) => ({ ...c, creatives: c.creatives.filter((cr) => cr.textureUrl === null || textureAllowed(cr.textureUrl, this.allowedImageOrigins)) }))
        .filter((c) => c.creatives.length > 0);
      if (safe.length < campaigns.length) errors.push(`ad manifest ${url}: ${campaigns.length - safe.length} campaign(s) with foreign image URLs skipped`);
      if (safe.length > 0) {
        // only companies whose posters actually run
        const running = new Set(safe.map((c) => c.companyId));
        this.sponsors = parseSponsors(json, this.allowedImageOrigins).filter((s) => running.has(s.id));
        // kept while its booking runs; a new player gets the company the backend assigns in turn (`zugeteilt`), else random
        const assigned = typeof json === 'object' && json !== null ? (json as { zugeteilt?: unknown }).zugeteilt : undefined;
        this.sponsor =
          this.sponsors.find((s) => s.id === previous) ??
          this.sponsors.find((s) => s.id === assigned) ??
          this.sponsors[Math.floor(this.random() * this.sponsors.length)] ??
          null;
        const own = this.sponsor ? coverEveryFormat(safe.filter((c) => c.companyId === this.sponsor!.id)) : [];
        // a sponsored game: only that company's posters; a manifest without sponsors (static file): the old mix with ructec
        this.delegate = own.length > 0 ? new StaticAdProvider(own, this.random) : new StaticAdProvider([...safe, ...houseShares(json)], this.random);
      }
      const session = typeof json === 'object' && json !== null ? (json as { sitzung?: unknown }).sitzung : undefined;
      if (typeof session === 'string' && /^[0-9a-f]{32}\.[0-9]{9,11}\.[0-9a-f]{64}$/.test(session)) this.session = session;
      return errors;
    } catch (error) {
      return [`ad manifest ${url}: ${error instanceof Error ? error.message : String(error)}`];
    } finally {
      clearTimeout(timer);
    }
  }

  select(slotType: AdSlotType, ctx: AdRequestContext): AdSelection | null {
    // a slot type no booked campaign covers still gets the house ad
    return this.delegate.select(slotType, ctx) ?? (this.delegate === this.fallback ? null : this.fallback.select(slotType, ctx));
  }

  recordImpression(campaignId: string): void {
    this.delegate.recordImpression(campaignId);
  }
}
